import { createContext, useContext, useEffect, useState } from "react";
import {
  deleteMappingById,
  deleteProjectById,
  getAllProjects,
  getMappingsByProjectId,
  addNewMapping,
} from "../../../api/mappingServices";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  setEditStateData,
  setWorkspaceComponent,
  setSelectedAction,
} from "../../../store/workspaceSlice";

const RulesContext = createContext();

export const RulesProvider = ({ children }) => {
  const [loading, setLoading] = useState(null);
  const [projects, setProjects] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [view, setview] = useState("projects");
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    fetchProjects();
  }, [location.pathname]);

  const fetchProjects = async () => {
    clearEditState();
    try {
      setLoading("fetchingProjects");
      const response = await getAllProjects();
      setProjects(response.data);
    } catch (error) {
      toast.error("Failed to fetch mappings");
    } finally {
      setLoading(null);
    }
  };

  // Fetch mappings for a specific project
  const fetchMappingsForProject = async (projectId) => {
    try {
      setview("mappings");
      setLoading("fetchingRules");
      const response = await getMappingsByProjectId(projectId);
      if (response.success) {
        setMappings(response.data);
      } else {
        toast.error("Failed to fetch mappings for this project");
      }
    } catch (error) {
      toast.error("Failed to fetch mappings");
    } finally {
      setLoading(null);
    }
  };

  const clearEditState = () => {
    dispatch(setEditStateData({ data: {}, type: "" }));
    dispatch(setWorkspaceComponent(""));
    dispatch(setSelectedAction(""));
  };

  const deleteMapping = async (ruleId) => {
    setLoading("deletingRule_" + ruleId);
    const response = await deleteMappingById(ruleId);
    if (response.success) {
      setMappings((prevRules) =>
        prevRules.filter((rule) => rule._id !== ruleId),
      );
      setLoading(null);
      toast.success("Deleted Successfully");
    }
    if (!response.success) {
      setLoading(null);
      toast.error("Failed to delete, try again");
    }
  };

  // Handle project selection
  const selectProject = async (project) => {
    setSelectedProject(project);
    await fetchMappingsForProject(project._id);
  };
  // Go back to projects view
  const backToProjects = () => {
    setSelectedProject(null);
    setMappings([]);
    setview("projects");
    fetchProjects();
  };

  const delete_project = async (projectID) => {
    try {
      setLoading("deletingProject_" + projectID);

      const response = await deleteProjectById(projectID);

      if (response.success) {
        setProjects((prevProjects) =>
          prevProjects.filter((project) => project._id !== projectID),
        );
        toast.success("Project deleted successfully");
      } else {
        toast.error("Failed to delete project");
      }
    } catch (error) {
      toast.error("An error occurred while deleting the project");
    }
  };

  const create_duplicate = async (data) => {
    try {
      const { createdBy, mappingData, mappingName, updatedBy, _id, ...rest } =
        data;

      const duplicate = {
        ...rest,
        fileName: data.mappingName + " (Copy)",
        rules: mappingData,
      };

      setLoading(`creatingDuplicate_${_id}`);
      const response = await addNewMapping(duplicate);
      if (response.success) {
        setMappings((prevMappings) => [...prevMappings, response.data]);
        toast.success("Mapping duplicated successfully");
      }
    } catch (error) {
      toast.error("An error occurred while duplicating the mapping");
    } finally {
      setLoading(null);
    }
  };

  const contextValue = {
    // Data
    projects,
    mappings,
    selectedProject,

    // State
    loading,
    view,

    // Actions
    setProjects,
    selectProject,
    backToProjects,
    deleteMapping,
    delete_project,
    create_duplicate,
  };

  return (
    <RulesContext.Provider value={contextValue}>
      {children}
    </RulesContext.Provider>
  );
};

// Custom hook to use the RulesContext
export const useRules = () => {
  return useContext(RulesContext);
};
